import { useCallback } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiClient } from '@/shared/api/http/client';
import { ENDPOINTS } from '@/shared/api/endpoints';
import { queryKeys } from '@/shared/api/queryKeys';
import { useShellStore } from '@/features/shell/model/shellStore';
import type {
  SystemObject,
  SystemObjectField,
  SystemObjectDetail,
  SystemObjectFilter,
  SystemRoleObject,
  SystemRoleObjectField,
  SystemObjectAction,
  SystemRoleObjectAction,
  SystemHtmlCardTemplate,
  SystemHtmlSummaryTemplate,
  SystemRazorTemplate,
  SystemNodeActionType,
  SystemScreen,
} from '@/shared/types/system';
import type { ReportAction } from '@/shared/api/reportApi';
import { useAuthStore } from '@/entities/session/model/authStore';

/** DAL `GET api/system/ObjectBundle/{objectId}` yanıtı (camelCase). */
export interface ObjectBundle {
  object: SystemObject;
  fields: SystemObjectField[];
  details: SystemObjectDetail[];
  filters: SystemObjectFilter[];
  roleObject: SystemRoleObject | null;
  roleFields: SystemRoleObjectField[];
  actions: SystemObjectAction[];
  roleActions: SystemRoleObjectAction[];
  reportActions: ReportAction[];
  formattingRules: unknown[];
  calculationRules: unknown[];
  nodeActionTypes: SystemNodeActionType[];
  htmlCardTemplate: SystemHtmlCardTemplate | null;
  htmlSummaryTemplate: SystemHtmlSummaryTemplate | null;
  razorTemplate: SystemRazorTemplate | null;
  lookupFieldIds: string[];
  /** ObjectActionId → hedef nesne özeti; işlem ikonunu türetmek için (bkz. deriveActionIcon). */
  actionTargets?: Record<string, { targetObjectId: string | null; objectTypeId: string | null; hasVisibleParameters: boolean }>;
}

/** DAL `GET api/system/ScreenBundle/{screenId}` yanıtı. */
export interface ScreenBundle extends ObjectBundle {
  screen: SystemScreen;
  detailObjects: Record<string, ObjectBundle>;
  styles: unknown[];
  stamp: string;
}

/**
 * Ekran paketi, ana nesnenin ve detayların NESNE paketlerini de taşıyor; onlar da
 * önbelleğe yazılır. SP parametre penceresi (useObjectBundle) ve kayıt formu aynı nesneyi
 * ikinci kez indiriyordu: SP ekranında demet indikten sonra pencere bir tur daha
 * "Hazırlanıyor…" bekletiyordu.
 */
export function seedObjectBundles(qc: QueryClient, bundle: ScreenBundle) {
  if (bundle.object?.objectId) qc.setQueryData<ObjectBundle>(queryKeys.meta.objectBundle(bundle.object.objectId), bundle);
  for (const detail of Object.values(bundle.detailObjects ?? {})) {
    if (detail.object?.objectId) qc.setQueryData<ObjectBundle>(queryKeys.meta.objectBundle(detail.object.objectId), detail);
  }
}

async function fetchScreenBundle(qc: QueryClient, screenId: string, roleId: string | null): Promise<ScreenBundle> {
  const bundle = await apiClient.get<ScreenBundle>(ENDPOINTS.SCREEN_BUNDLE(screenId, roleId), {
    octo: { expectSuccess: false },
  });
  seedObjectBundles(qc, bundle);
  return bundle;
}

/** Ekranın tüm metadata'sı tek çağrıda; tasarım modunda önbellek bypass. */
export function useScreenBundle(screenId: string | null | undefined) {
  const qc = useQueryClient();
  const designMode = useShellStore((s) => s.designMode);
  const roleId = useAuthStore((s) => s.user?.roleId ?? null);
  return useQuery({
    queryKey: queryKeys.meta.screenBundle(screenId ?? ''),
    queryFn: () => fetchScreenBundle(qc, screenId!, roleId),
    enabled: !!screenId,
    staleTime: designMode ? 0 : Infinity,
    gcTime: 24 * 60 * 60_000,
  });
}

/**
 * Ekran paketini kullanıcı daha tıklamadan indirir (menüde üzerine gelince).
 *
 * Açılışın en uzun halkası demet; imleç menü satırına geldiğinde başlatmak tıklamaya
 * kadar geçen yüz milisaniyeleri kazandırıyor ve SP ekranında parametre penceresi
 * "Ekran açılıyor…" örtüsü yerine doğrudan açılıyor. Önbellekte varsa hiçbir şey yapmaz;
 * tasarım modunda (önbellek kapalı) devre dışı.
 */
export function usePrefetchScreenBundle() {
  const qc = useQueryClient();
  const designMode = useShellStore((s) => s.designMode);
  const roleId = useAuthStore((s) => s.user?.roleId ?? null);
  return useCallback(
    (screenId: string | null | undefined) => {
      if (!screenId || designMode) return;
      void qc.prefetchQuery({
        queryKey: queryKeys.meta.screenBundle(screenId),
        queryFn: () => fetchScreenBundle(qc, screenId, roleId),
        staleTime: Infinity,
      });
    },
    [qc, designMode, roleId],
  );
}

export function useObjectBundle(objectId: string | null | undefined) {
  const designMode = useShellStore((s) => s.designMode);
  const roleId = useAuthStore((s) => s.user?.roleId ?? null);
  return useQuery({
    queryKey: queryKeys.meta.objectBundle(objectId ?? ''),
    queryFn: () =>
      apiClient.get<ObjectBundle>(ENDPOINTS.OBJECT_BUNDLE(objectId!, roleId), {
        octo: { expectSuccess: false },
      }),
    enabled: !!objectId,
    staleTime: designMode ? 0 : Infinity,
    gcTime: 24 * 60 * 60_000,
  });
}
